import React, { useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { IFRAME_URLS } from '../../constants/constant';
import { isOriginAllowed } from '../../config/allowedOrigins';
import { getValidMaiaAccessToken } from '../../services/auth/brokerSession';

interface IframeData {
  icd10Enabled: boolean;
  cptEnabled: boolean;
  modifiersEnabled: boolean;
  noteEnabled: boolean;
  icd10Codes: string;
  cptCodes: string;
  modifierCodes: string;
  noteText: string;
  // Advanced fields
  payerEnabled: boolean;
  dateOfServiceEnabled: boolean;
  feeEnabled: boolean;
  claimDateEnabled: boolean;
  renderingProviderEnabled: boolean;
  billingProviderEnabled: boolean;
  supervisingProviderEnabled: boolean;
  referringProviderEnabled: boolean;
  patientStatusEnabled: boolean;
  unitsEnabled: boolean;
  payerValue: string;
  dateOfServiceValue: string;
  feeValue: string;
  claimDateValue: string;
  renderingProviderValue: string;
  billingProviderValue: string;
  supervisingProviderValue: string;
  referringProviderValue: string;
  patientStatusValue: string;
  unitsValue: string;
}

interface IframeMessage {
  type: string;
  payload?: IframeData;
}

interface AuthData {
  idToken: string;
  userEmail: string;
  isAthenaOne: boolean;
  showLogoutButton: boolean;
  userFirstName: string;
  userLastName: string;
}

interface CodeGuardIframeProps {
  data: IframeData;
  onDataReceived?: (data: IframeMessage) => void;
}

const CodeGuardIframe: React.FC<CodeGuardIframeProps> = ({
  data,
  onDataReceived,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { user } = useSelector((state: RootState) => state.user);

  // Use sandbox URL for non-localhost environments (staging, production, dev deployments)
  // Use local URL only for localhost development
  // const iframeUrl =
  //   window.location.hostname === 'localhost'
  //     ? IFRAME_URLS.CODE_GUARD_LOCAL
  //     : IFRAME_URLS.CODE_GUARD;

  const iframeUrl =
    window.location.hostname === 'localhost'
      ? IFRAME_URLS.CODE_GUARD_LOCAL
      : (import.meta.env.VITE_IFRAME_URLS ?? IFRAME_URLS.CODE_GUARD);

  // Get authentication data. The embedded sandbox forwards `idToken` as its
  // bearer to the Maia backends, so it must be the broker-minted Maia JWT (the
  // backends dual-accept it) — never an Auth0 token. The field name is kept for
  // the postMessage contract with the sandbox app.
  const getAuthData = useCallback(async (): Promise<AuthData> => {
    const idToken = (await getValidMaiaAccessToken()) || '';
    const userEmail = user?.email || '';

    return {
      idToken,
      userEmail,
      isAthenaOne: false,
      showLogoutButton: false,
      userFirstName: user?.firstName || '',
      userLastName: user?.lastName || '',
    };
  }, [user]);

  // Send data to iframe when data changes
  const sendDataToIframe = useCallback(() => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      console.log('=== SENDING DATA TO IFRAME ===');
      console.log('Data being sent:', data);

      iframeRef.current.contentWindow.postMessage(
        {
          type: 'MAIA_SEND_DATA',
          payload: data,
        },
        iframeUrl,
      );
    }
  }, [data, iframeUrl]);

  // Send authentication data to iframe
  const sendAuthDataToIframe = useCallback(async () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      const authData = await getAuthData();
      console.log('=== SENDING AUTH DATA TO IFRAME ===');
      console.log('Auth data being sent:', {
        ...authData,
        idToken: authData.idToken ? '[REDACTED]' : 'NOT_FOUND',
      });

      iframeRef.current.contentWindow.postMessage(
        {
          type: 'MAIA_AUTH_DATA',
          payload: authData,
        },
        iframeUrl,
      );
    }
  }, [getAuthData, iframeUrl]);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log('=== MESSAGE RECEIVED FROM IFRAME ===');
      console.log('Origin:', event.origin);
      console.log('Data:', event.data);

      // Check origin for security against the configured allowlist
      if (!isOriginAllowed(event.origin)) {
        console.warn(
          'Rejected message from unauthorized origin:',
          event.origin,
        );
        return;
      }

      if (event.data && event.data.type === 'IFRAME_READY') {
        console.log('Iframe is ready, sending auth data...');
        sendAuthDataToIframe();
      }

      if (event.data && event.data.type === 'MAIA_REQUEST_DATA') {
        console.log('CodeGuard is requesting data, sending response...');
        sendDataToIframe();
      }

      if (event.data && event.data.type === 'MAIA_REQUEST_AUTH') {
        console.log('CodeGuard is requesting auth data, sending response...');
        sendAuthDataToIframe();
      }

      if (event.data && event.data.type === 'DATA_FROM_IFRAME') {
        onDataReceived?.(event.data.payload);
      }

      if (event.data && event.data.type === 'MAIA_CLEAR_FIELDS') {
        console.log('Processing MAIA_CLEAR_FIELDS message:', event.data);
        onDataReceived?.(event.data);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onDataReceived, sendDataToIframe, sendAuthDataToIframe]);

  // Send auth data immediately when component mounts
  useEffect(() => {
    // Small delay to ensure iframe is loaded
    const timer = setTimeout(() => {
      sendAuthDataToIframe();
    }, 100);

    return () => clearTimeout(timer);
  }, [sendAuthDataToIframe]);

  // Send data when component mounts or data changes
  useEffect(() => {
    // Small delay to ensure iframe is loaded
    const timer = setTimeout(() => {
      sendDataToIframe();
    }, 200);

    return () => clearTimeout(timer);
  }, [sendDataToIframe]);

  return (
    <div className="w-full h-full">
      <iframe
        ref={iframeRef}
        src={iframeUrl}
        title="CodeGuard Check Interface"
        className="w-full h-full"
        sandbox="allow-scripts allow-same-origin allow-forms"
        onLoad={() => {
          // Send auth data when iframe finishes loading
          setTimeout(() => {
            sendAuthDataToIframe();
          }, 100);
        }}
      />
    </div>
  );
};

export default CodeGuardIframe;
